import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import SwapVertRounded from '@mui/icons-material/SwapVertRounded';
import { useFeature } from '../../ui/FeaturesProvider';
import { useBoardId } from '../../ui/BootstrapProvider';
import { useNavigate } from 'react-router-dom';
import { EmptyState, ToolbarButton } from '../../ui-kit';
import { fetchScorecard, type FetchOpts } from '../../services/contactOptimizationService';
import type {
  ScorecardAgentValue,
  ScorecardMetric,
  ScorecardResponse,
} from '../../types/contactOptimization';
import ContactOptimizationNav from './ContactOptimizationNav';
import ScorecardPeriodPicker, { defaultScorecardRange, type ScorecardRange } from './ScorecardPeriodPicker';
import ScorecardCohort from './ScorecardCohort';
import {
  REFRESHING_BANNER_MESSAGE,
  RefreshProvider,
  useRefreshController,
  useRefreshRegistration,
  useReportFreshness,
} from './RefreshController';
import { usePageBanner } from '../../ui/PageBannerProvider';

/**
 * the customer's own scorecard (TY_Scorecard.xlsx): 24 KPIs in the sheet's
 * five pillars, each with a team figure, a 13-week trend and a per-agent
 * breakdown.
 *
 * Grouped by the sheet's pillars rather than the Primer's four outcomes,
 * with a column naming the outcome each metric serves -- the two
 * documents disagree on where five metrics belong, and a column shows
 * that permanently instead of hiding it behind a view toggle.
 *
 * Metric names carry the calculation in plain English on hover. That is
 * the contract with the customer about what each number means, and it comes
 * from the backend (`scorecard.py`) so the wording lives next to the
 * code that computes it.
 */

/** Shared by the header and every row so the two can never drift.
 * The metric column is capped rather than a bare `1fr`: left flexible it
 * swallowed ~1300px on a wide screen, stranding the value, sparkline and
 * button in a cramped huddle at the right edge. */
const COLUMNS = {
  xs: '1fr 96px',
  sm: '1fr 100px 190px',
  md: 'minmax(220px, 1fr) 168px 100px 156px 190px',
} as const;

const SPARK_W = 148;
const SPARK_H = 22;
const SPARK_PAD = 3;

/** Below a third of the median denominator an agent's rate rests on so
 * little volume that reading it like the others misleads. Dimmed rather
 * than dropped -- hiding rows would be us deciding what the customer may see. */
const THIN_DENOMINATOR_RATIO = 3;

function formatValue(value: number | null, format: ScorecardMetric['format']): string {
  if (value === null || value === undefined) return '—';
  switch (format) {
    case 'percent':
      return `${Math.abs(value) < 10 ? value.toFixed(2) : value.toFixed(1)}%`;
    case 'money':
      return `$${Math.round(value).toLocaleString()}`;
    case 'days':
      return `${Math.round(value)}d`;
    case 'count':
      return Math.round(value).toLocaleString();
    default:
      return value.toFixed(2);
  }
}

/** A trend point's own date range, e.g. "10 – 12 Aug 2026". */
function formatRange(start: string, end: string): string {
  const s = new Date(`${start}T00:00`);
  const e = new Date(`${end}T00:00`);
  const year = e.toLocaleDateString(undefined, { year: 'numeric' });
  if (start === end) {
    return `${e.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${year}`;
  }
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const from = s.toLocaleDateString(undefined, sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' });
  const to = e.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${from} – ${to} ${year}`;
}

/**
 * The trend, with a readout per point.
 *
 * At 148px wide a 13-point line is too small to read a value off, so
 * every point gets a full-height hit target (wider than the visible mark)
 * and the tooltip follows the cursor with that point's date range and
 * value. Without it the line only shows shape, and the shape is the least
 * useful thing about it.
 */
function Sparkline({
  trend,
  periods,
  format,
  description,
}: {
  trend: (number | null)[];
  periods: { start: string; end: string }[];
  format: ScorecardMetric['format'];
  description: string;
}) {
  const theme = useTheme();
  const [hover, setHover] = React.useState<number | null>(null);

  const points = trend
    .map((v, i) => ({ i, v }))
    .filter((p): p is { i: number; v: number } => p.v !== null);
  if (points.length < 2) return null;

  const values = points.map((p) => p.v);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const x = (i: number) => SPARK_PAD + (i / (trend.length - 1)) * (SPARK_W - 2 * SPARK_PAD);
  const y = (v: number) => SPARK_H - SPARK_PAD - ((v - lo) / span) * (SPARK_H - 2 * SPARK_PAD);
  const d = points.map((p, k) => `${k ? 'L' : 'M'}${x(p.i).toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  const slot = SPARK_W / trend.length;

  const hovered = hover !== null ? trend[hover] : null;
  const title =
    hover !== null && hovered !== null && periods[hover] ? (
      <Box sx={{ py: 0.25 }}>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
          {formatRange(periods[hover].start, periods[hover].end)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: 13 }}>
          {formatValue(hovered, format)}
        </Typography>
      </Box>
    ) : (
      description
    );

  return (
    <Tooltip title={title} followCursor placement="top">
      <Box
        component="svg"
        width={SPARK_W}
        height={SPARK_H}
        viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
        sx={{ cursor: 'crosshair', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        <path
          d={d}
          fill="none"
          stroke={alpha(theme.palette.text.secondary, 0.65)}
          strokeWidth={1.4}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={x(last.i)} cy={y(last.v)} r={2.4} fill={theme.palette.primary.main} />
        {hover !== null && hovered !== null && (
          <>
            <line
              x1={x(hover)}
              y1={0}
              x2={x(hover)}
              y2={SPARK_H}
              stroke={alpha(theme.palette.text.primary, 0.25)}
              strokeWidth={1}
            />
            <circle cx={x(hover)} cy={y(hovered)} r={3} fill={theme.palette.primary.main} />
          </>
        )}
        {/* Hit targets are a full slot wide and full height -- the marks
            themselves are a few pixels and would be nearly unhittable. */}
        {trend.map((v, i) =>
          v === null ? null : (
            <rect
              key={i}
              x={x(i) - slot / 2}
              y={0}
              width={slot}
              height={SPARK_H}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          ),
        )}
      </Box>
    </Tooltip>
  );
}

function AgentBreakdown({ metric }: { metric: ScorecardMetric }) {
  const theme = useTheme();
  const [sortBy, setSortBy] = React.useState<'volume' | 'value'>('volume');
  // Memoised, not `metric.agents ?? []` inline: that allocates a new
  // array every render and would invalidate all three memos below it.
  const agents = React.useMemo(() => metric.agents ?? [], [metric.agents]);

  // Volume first. With no minimum-volume floor anywhere in this feature,
  // ranking by value puts whoever happened to have three leads at the
  // top of every list -- true, but not a leaderboard.
  const rows = React.useMemo(() => {
    const copy = [...agents];
    copy.sort((a, b) => (sortBy === 'volume' ? b.denominator - a.denominator : b.value - a.value));
    return copy;
  }, [agents, sortBy]);

  const maxAbs = React.useMemo(
    () => Math.max(...agents.map((a) => Math.abs(a.value)), 1),
    [agents],
  );
  const medianDenominator = React.useMemo(() => {
    if (!agents.length) return 0;
    const sorted = agents.map((a) => a.denominator).sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }, [agents]);

  if (!agents.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        This metric is measured for the team as a whole.
      </Typography>
    );
  }

  // Every metric with a breakdown ships a label (the backend asserts it).
  // The fallback only shows for a payload cached before that field
  // existed, so it says plainly what the column holds rather than
  // describing it -- prose here reads as a second value column.
  const denomLabel = metric.denominator_label ?? 'count';
  const thinThreshold = medianDenominator / THIN_DENOMINATOR_RATIO;
  const thinCount = agents.filter((a) => a.denominator < thinThreshold).length;
  const GRID = { xs: '1fr 82px 72px', sm: '158px 82px 1fr 108px' } as const;

  // Column headers double as the sort control. A separate "sorted by
  // volume / by value" button named neither column, so the words meant
  // nothing until you guessed which number they referred to.
  const SortHead = ({ by, children, align }: { by: 'volume' | 'value'; children: React.ReactNode; align: 'left' | 'right' }) => (
    <Box
      component="button"
      onClick={() => setSortBy(by)}
      sx={(t) => ({
        border: 'none',
        background: 'none',
        p: 0,
        cursor: 'pointer',
        font: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        color: sortBy === by ? t.palette.text.primary : t.palette.text.disabled,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        '&:hover': { color: t.palette.primary.main },
      })}
    >
      {children}
      <SwapVertRounded sx={{ fontSize: 13, opacity: sortBy === by ? 1 : 0.4 }} />
    </Box>
  );

  return (
    <Box>
      <Box
        sx={(t) => ({
          display: 'grid',
          gridTemplateColumns: GRID,
          gap: 1.25,
          alignItems: 'center',
          pb: 0.75,
          mb: 0.75,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
        })}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }} color="text.disabled">
          {rows.length} agents
        </Typography>
        <SortHead by="value" align="right">
          {metric.name}
        </SortHead>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
        <SortHead by="volume" align="right">
          {denomLabel}
        </SortHead>
      </Box>

      <Stack spacing={0.25}>
        {rows.map((row: ScorecardAgentValue) => {
          const thin = medianDenominator > 0 && row.denominator < thinThreshold;
          return (
            <Box key={row.agent} sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 1.25, alignItems: 'center' }}>
              <Typography variant="caption" noWrap color={thin ? 'text.disabled' : 'text.secondary'}>
                {row.agent}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  color: thin ? 'text.disabled' : 'text.primary',
                }}
              >
                {formatValue(row.value, metric.format)}
              </Typography>
              <Box
                sx={(t) => ({
                  display: { xs: 'none', sm: 'block' },
                  height: 6,
                  borderRadius: 3,
                  bgcolor: t.tokens.border.subtle,
                  overflow: 'hidden',
                })}
              >
                <Box
                  sx={(t) => ({
                    height: '100%',
                    width: `${Math.max((Math.abs(row.value) / maxAbs) * 100, 1.5)}%`,
                    borderRadius: 3,
                    bgcolor: alpha(t.palette.text.secondary, thin ? 0.18 : 0.35),
                  })}
                />
              </Box>
              <Tooltip
                arrow
                title={
                  thin
                    ? `Only ${row.denominator.toLocaleString()} ${denomLabel} — too few for this figure to be reliable, so the row is dimmed. It is shown in full rather than hidden.`
                    : `${row.denominator.toLocaleString()} ${denomLabel}`
                }
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    cursor: 'help',
                    color: thin ? theme.palette.warning.main : 'text.disabled',
                  }}
                >
                  {row.denominator.toLocaleString()}
                </Typography>
              </Tooltip>
            </Box>
          );
        })}
      </Stack>

      {thinCount > 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.25, lineHeight: 1.45 }}>
          {thinCount === 1 ? '1 agent is' : `${thinCount} agents are`} dimmed: their figure rests on far
          fewer {denomLabel} than the rest of the team, so it swings wildly on one or two outcomes.
          Nothing is hidden or excluded — the team figure above still includes them.
        </Typography>
      )}
    </Box>
  );
}

function MetricRow({
  metric,
  trendDescription,
  trendPeriods,
}: {
  metric: ScorecardMetric;
  trendDescription: string;
  trendPeriods: { start: string; end: string }[];
}) {
  const [open, setOpen] = React.useState(false);
  const blocked = Boolean(metric.unavailable_reason);
  const outcomes = metric.primer_outcomes;
  const dualOutcome = outcomes.length > 1;

  return (
    <Box
      sx={(t) => ({
        borderTop: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: blocked ? alpha(t.palette.text.primary, 0.015) : 'transparent',
      })}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: COLUMNS,
          gap: 1.75,
          alignItems: 'center',
          px: 2,
          py: 1,
          minHeight: 46,
        }}
      >
        <Tooltip
          arrow
          placement="bottom-start"
          slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}
          disableHoverListener={!metric.how}
          title={
            metric.how ? (
              <Box sx={{ py: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.7, mb: 0.25 }}
                >
                  How this is calculated
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                  {metric.how}
                </Typography>
              </Box>
            ) : (
              ''
            )
          }
        >
          <Typography
            variant="body2"
            noWrap
            sx={(t) => ({
              color: blocked ? t.palette.text.disabled : t.palette.text.secondary,
              cursor: metric.how ? 'help' : 'default',
              textDecoration: metric.how ? 'underline dotted' : 'none',
              textDecorationColor: t.tokens.border.subtle,
              textUnderlineOffset: 3,
              width: 'fit-content',
              maxWidth: '100%',
              '&:hover': metric.how ? { color: t.palette.text.primary } : undefined,
            })}
          >
            {metric.name}
          </Typography>
        </Tooltip>

        {blocked ? (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ gridColumn: { xs: '1 / -1', sm: '2 / -1' }, lineHeight: 1.35 }}
          >
            {metric.unavailable_reason}
          </Typography>
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{
                display: { xs: 'none', md: 'block' },
                color: dualOutcome ? 'warning.main' : 'text.secondary',
                lineHeight: 1.3,
              }}
            >
              {outcomes.join(' + ')}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontWeight: 700,
                fontSize: 14,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatValue(metric.value, metric.format)}
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', height: SPARK_H }}>
              {metric.trend ? (
                <Sparkline
                  trend={metric.trend}
                  periods={trendPeriods}
                  format={metric.format}
                  description={`${trendDescription}. Hover a point for its dates and value.`}
                />
              ) : null}
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
              <Button
                size="small"
                variant={open ? 'contained' : 'outlined'}
                onClick={() => setOpen((v) => !v)}
                endIcon={open ? undefined : <ArrowForwardRounded sx={{ fontSize: 13 }} />}
                sx={{
                  fontSize: 11.5,
                  fontWeight: 500,
                  py: 0.5,
                  px: 1.5,
                  width: '100%',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  '& .MuiButton-endIcon': { ml: 0.75 },
                }}
              >
                {open ? 'Hide agent breakdown' : metric.agents?.length ? 'View agent breakdown' : 'Team level only'}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {!blocked && (
        <Collapse in={open} unmountOnExit>
          <Box sx={(t) => ({ px: 2, pb: 2, bgcolor: alpha(t.palette.text.primary, 0.02) })}>
            {metric.note && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', pt: 1.5, lineHeight: 1.45, maxWidth: '82ch' }}>
                {metric.note}
              </Typography>
            )}
            <Box sx={{ pt: 1.5 }}>
              <AgentBreakdown metric={metric} />
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export default function ScorecardPage() {
  // Where the workspace has the Scorecard board, this tab points there rather
  // than showing a second scorecard: the two were built separately and do not
  // always agree (Not Interested % being the case that surfaced it). The rest
  // of Contact Pulse's tabs stay reachable from the bar above.
  const scorecardBoard = useBoardId('sales_scorecard');
  if (scorecardBoard) return <ScorecardMoved boardId={scorecardBoard} />;
  return (
    <RefreshProvider>
      <ScorecardPageContent />
    </RefreshProvider>
  );
}

function ScorecardMoved({ boardId }: { boardId: string }) {
  const navigate = useNavigate();
  return (
    <Box>
      <ContactOptimizationNav active="scorecard" />
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
        <Box sx={{ maxWidth: 520, width: '100%' }}>
          <EmptyState
            title="The Scorecard has moved"
            message="It now has a page of its own in the sidebar, with the same measures by cohort and by agent."
            action={
              <ToolbarButton
                variant="brand"
                icon={<ArrowForwardRounded />}
                onClick={() => navigate(`/dashboard/${boardId}`)}
              >
                Open the Scorecard
              </ToolbarButton>
            }
          />
        </Box>
      </Box>
    </Box>
  );
}

function ScorecardPageContent() {
  const feature = useFeature('contact_optimization');
  const [data, setData] = React.useState<ScorecardResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [range, setRange] = React.useState<ScorecardRange>(() => defaultScorecardRange());

  // Only the default range is server-cached (see the router): sending its
  // dates explicitly would bypass that cache and pay a full recompute for
  // the view most people land on, so the default is sent as no dates.
  const isDefaultRange = React.useMemo(() => {
    const d = defaultScorecardRange();
    return range.start === d.start && range.end === d.end;
  }, [range]);

  const load = React.useCallback(
    (opts?: FetchOpts) => {
      setLoading(true);
      setError(null);
      return fetchScorecard(
        isDefaultRange ? undefined : { startDate: range.start, endDate: range.end },
        opts,
      )
        .then(setData)
        .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load the Scorecard'))
        .finally(() => setLoading(false));
    },
    [isDefaultRange, range.start, range.end],
  );

  React.useEffect(() => {
    if (!feature.enabled) return;
    load();
  }, [feature.enabled, load]);

  // Wires this page into the tab bar's shared Refresh button and its
  // "Data as of ..." stamp, the same way every other tab does.
  useRefreshRegistration(React.useCallback(() => load({ refresh: true }), [load]));
  useReportFreshness(data?.computed_at);

  // Changing the period refetches, and that is not a "refresh" -- so
  // `RefreshProvider` never raises its banner for it, leaving the table
  // looking final while it is actually stale. Drive the same global
  // status strip, with the same wording, that the Refresh button uses:
  // to the reader both are the identical state ("this page is
  // re-fetching"), and a second phrase would imply a difference that
  // isn't there. Suppressed while the provider is already showing it,
  // since both write to one shared banner.
  const refreshing = useRefreshController()?.refreshing ?? false;
  usePageBanner(
    loading && data && !refreshing ? { message: REFRESHING_BANNER_MESSAGE, tone: 'info' } : null,
  );

  if (!feature.enabled) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <ContactOptimizationNav active="scorecard" />
        <Alert severity="info">
          Contact Optimization is not turned on for this workspace. Enable it under Settings &rarr; Features.
        </Alert>
      </Box>
    );
  }

  if (loading && !data) {
    return (
      <Box>
        <ContactOptimizationNav active="scorecard" />
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress size={28} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <ContactOptimizationNav active="scorecard" />

      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 650 }}>
          Scorecard
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <ScorecardPeriodPicker
            value={range}
            label={data?.period.label}
            trendDescription={data?.trend.description}
            disabled={loading}
            onChange={setRange}
          />
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={() => load({ refresh: true })}>Retry</Button>}>
          {error}
        </Alert>
      )}


      {data && (
        <Paper variant="outlined" sx={{ overflow: 'hidden', maxWidth: 1180 }} aria-busy={loading}>
          <Box
            sx={(t) => ({
              display: 'grid',
              gridTemplateColumns: COLUMNS,
              gap: 1.75,
              alignItems: 'center',
              px: 2,
              height: 34,
              bgcolor: alpha(t.palette.text.primary, 0.025),
              borderBottom: `1px solid ${t.tokens.border.subtle}`,
            })}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase' }} color="text.disabled">
              Metric
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: { xs: 'none', md: 'block' }, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase' }}
              color="text.disabled"
            >
              Primer outcome
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', textAlign: 'right' }}
              color="text.disabled"
            >
              This period
            </Typography>
            <Tooltip arrow title={data.trend.description}>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  display: { xs: 'none', md: 'block' },
                  fontWeight: 700,
                  letterSpacing: 0.7,
                  textTransform: 'uppercase',
                  cursor: 'help',
                }}
                color="text.disabled"
              >
                {data.trend.short_label}
              </Typography>
            </Tooltip>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
          </Box>

          {data.pillars.map((pillar) => (
            <Box key={pillar.name}>
              <Box
                sx={(t) => ({
                  px: 2,
                  py: 1.25,
                  bgcolor: alpha(t.palette.text.primary, 0.025),
                  borderTop: `1px solid ${t.tokens.border.subtle}`,
                  borderBottom: `1px solid ${t.tokens.border.subtle}`,
                })}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  {pillar.name}{' '}
                  <Typography component="span" variant="caption" color="text.disabled" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                    ({pillar.owner})
                  </Typography>
                </Typography>
              </Box>
              {pillar.metrics.map((metric) => (
                <MetricRow
                  key={metric.id}
                  metric={metric}
                  trendDescription={data.trend.description}
                  trendPeriods={data.trend.periods}
                />
              ))}
            </Box>
          ))}
        </Paper>
      )}

      {/* Carries its own date range, deliberately independent of the period
          picker above: the pillars answer "how did this period go", the
          cohort answers "how fast do we reach a day's leads", and those are
          rarely the same window. */}
      <ScorecardCohort />
    </Box>
  );
}
